import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useFormatting } from '@ayeldo/frontend-core/hooks/useFormatting';

import { useIdpSession } from '../../hooks/useIdpSession';
import { adminApi, type IdentitySummary, type Pagination } from '../../services/admin-api';
import { APIError } from '../../services/idp-api';

export function UsersPage() {
  const { t } = useTranslation('idp');
  const navigate = useNavigate();
  const { formatDate } = useFormatting();
  const { isAuthenticated, isLoading: sessionLoading } = useIdpSession();
  const [identities, setIdentities] = useState<IdentitySummary[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionLoading && !isAuthenticated) {
      navigate('/auth/signin', { replace: true });
    }
  }, [isAuthenticated, sessionLoading, navigate]);

  useEffect(() => {
    let isMounted = true;

    async function load() {
      setIsLoading(true);
      setErrorMessage(null);
      try {
        const result = await adminApi.listIdentities(cursor);
        if (!isMounted) return;
        setIdentities((prev) =>
          cursor !== undefined && cursor !== '' ? [...prev, ...result.data] : result.data
        );
        setPagination(result.pagination);
      } catch (error) {
        if (!isMounted) return;
        if (error instanceof APIError) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage(t('users.errors.default'));
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    void load();

    return () => {
      isMounted = false;
    };
  }, [cursor, t]);

  if (sessionLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h5" component="h1" fontWeight={700}>
        {t('users.title')}
      </Typography>

      {errorMessage !== null ? <Alert severity="error">{errorMessage}</Alert> : null}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('users.tableHeaders.name')}</TableCell>
              <TableCell>{t('users.tableHeaders.email')}</TableCell>
              <TableCell>{t('users.tableHeaders.verified')}</TableCell>
              <TableCell>{t('users.tableHeaders.created')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {identities.map((identity) => (
              <TableRow key={identity.identityId}>
                <TableCell>{identity.name}</TableCell>
                <TableCell>{identity.email}</TableCell>
                <TableCell>
                  <Chip
                    label={
                      identity.emailVerified ? t('users.verifiedLabel') : t('users.unverifiedLabel')
                    }
                    color={identity.emailVerified ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{formatDate(identity.createdAt)}</TableCell>
              </TableRow>
            ))}
            {identities.length === 0 && !isLoading && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  {t('users.emptyMessage')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {isLoading && (
        <Box display="flex" justifyContent="center">
          <CircularProgress size={24} />
        </Box>
      )}

      {pagination?.hasMore && !isLoading && (
        <Box display="flex" justifyContent="center">
          <Button
            variant="outlined"
            onClick={() => {
              setCursor(pagination.nextCursor ?? undefined);
            }}
          >
            {t('users.loadMoreButton')}
          </Button>
        </Box>
      )}
    </Stack>
  );
}
